import {useContext, useEffect, useId, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import Button from "../../../../components/Button.jsx";
import useDownloadState from "../../../../api/utilities/useDownloadState.js";
import LoadingComponent from "../../../../components/LoadingComponent.jsx";
import {GlobalContext} from "../../../../utils/context/GlobalContextProvider.jsx";
import {ProjectIntegrationsApi} from "../../../../api/authority/generated/index";
import toast from "react-hot-toast";
import {useNavigate} from "react-router-dom";
import {useErrorTranslation} from "../../../../api/utilities/translateErrorMessage.js";
import useAuthorityConfiguration from "../../../../api/utilities/useAuthorityConfiguration.js";
import Select from "react-select";
import TextareaWithClipboard from "../../../../components/TextareaWithClipboard.jsx";
import useIntegrationScript from "../IntegrationFormInput/useIntegrationScript.js";

function BsShopIntegrationFormInput({type, label, value, note, onChange}) {

    let id = useId();

    return <div>
        <div className={'flex gap-4'}>
            <label className='w-4/12 my-auto text-right' htmlFor={'mysql-lead-' + id}>
                {label}:
            </label>
            <input
                type={type}
                id={'mysql-lead-' + id}
                className='w-8/12 form-input'
                value={value}
                onChange={(e) => onChange(e.target.value)}
            />
        </div>
        <div className={'flex gap-4'}>
            <div className={'w-4/12'}></div>
            <div className={'w-8/12'}>{note}</div>
        </div>
    </div>;
}

export default function BsShopIntegrationForm({project, projectIntegration}) {

    let {t} = useTranslation();
    let {et} = useErrorTranslation();
    let navigate = useNavigate();
    let integrationScript = useIntegrationScript('bsshop', projectIntegration?.id);

    let [bsShopSettings, setBsShopSettings] = useState({
        'hostname': projectIntegration?.hostname ?? '',
        'port': projectIntegration?.port ?? '1433',
        'username': projectIntegration?.username ?? '',
        'password': '',
        'databaseName': projectIntegration?.databaseName ?? '',
        'skippedEmailAddressList': projectIntegration?.skippedEmailAddressList ?? '',
        'b2cCentres': projectIntegration?.b2cCentres ?? [],
        'b2bCentres': projectIntegration?.b2bCentres ?? [],
    });

    let {resetCurrentUserProjects} = useContext(GlobalContext);

    let authorityConfiguration = useAuthorityConfiguration();

    let [buttonClicked, setButtonClicked] = useState(false);
    let [state, setDownloading, setResult, setError] = useDownloadState();

    useEffect(() => {

        if (!buttonClicked) {
            return;
        }

        setButtonClicked(false);

        setDownloading(true);

        new ProjectIntegrationsApi(authorityConfiguration)
            .bSShopIntegration({
                projectId: project.id,
                integrationId: projectIntegration?.id ?? null,
                bsShopIntegrationInput: bsShopSettings,
            })
            .then((result) => {
                navigate('/project/' + project.id + '/settings/integrations?integrationId=' + result.id);
                resetCurrentUserProjects();

                if (projectIntegration === null) {
                    toast.success(t('created'));
                } else {
                    if (result.errors.length > 0) {
                        toast.success(t('saved-with-errors'));
                    } else {
                        toast.success(t('saved'));
                    }
                }
            })
            .catch(e => {
                et(e).then(toast.error);
            })
            .finally(() => setDownloading(false));

    }, [buttonClicked]);

    let centreOptions = useMemo(() => {
        let result = [];

        for (let centreId in projectIntegration?.knownCentres ?? []) {
            let centreName = projectIntegration?.knownCentres[centreId];

            result.push({
                value: centreId,
                label: centreName
            });
        }

        return result;
    }, [projectIntegration?.knownCentres]);

    let defaultB2BCentres = useMemo(() => {
        let result = [];

        for (let centreId in projectIntegration?.knownCentres ?? []) {
            let centreName = projectIntegration?.knownCentres[centreId];

            if (
                projectIntegration.b2bCentres.includes(centreId.toString())
                || projectIntegration.b2bCentres.includes(parseInt(centreId))
            ) {
                result.push({
                    value: centreId,
                    label: centreName
                });
            }

        }

        return result;
    }, [projectIntegration?.knownCentres, projectIntegration?.b2bCentres]);


    let defaultB2CCentres = useMemo(() => {
        let result = [];

        for (let centreId in projectIntegration?.knownCentres ?? []) {
            let centreName = projectIntegration?.knownCentres[centreId];

            if (
                projectIntegration.b2cCentres.includes(centreId.toString())
                || projectIntegration.b2cCentres.includes(parseInt(centreId))
            ) {
                result.push({
                    value: centreId,
                    label: centreName
                });
            }
        }

        return result;
    }, [projectIntegration?.knownCentres, projectIntegration?.b2cCentres]);

    function setSelectedB2CCentres(value) {
        let selectedCentres = [];

        for (let valueObject of Object.values(value)) {
            selectedCentres.push(valueObject.value);
        }

        setBsShopSettings(
            (bsShopSettings) => (
                {...bsShopSettings, b2cCentres: selectedCentres}
            )
        );
    }

    function setSelectedB2BCentres(value) {
        let selectedCentres = [];

        for (let valueObject of Object.values(value)) {
            selectedCentres.push(valueObject.value);
        }

        setBsShopSettings(
            (bsShopSettings) => (
                {...bsShopSettings, b2bCentres: selectedCentres}
            )
        );
    }

    // noinspection JSUnresolvedReference
    return (
        <div>
            {projectIntegration !== null && <>
                <div className={'flex flex-col p-2 border-1 border-slate-400 rounded mb-8'}>
                    <strong className={'mt-2'}>{t('integration.bsshop.script-title')}:</strong>
                    <TextareaWithClipboard
                        value={integrationScript}
                        showButton={true}
                        textareaClasses={'min-h-[158px]'}
                    />
                    <small className={'mt-2'}>{t('integration.eprivacy-warning')}</small>
                </div>
            </>}

            <LoadingComponent loading={state.downloading}>
                <div className={'flex flex-col gap-2'}>
                    <BsShopIntegrationFormInput
                        type={'text'}
                        label={t('mysql.hostname')}
                        value={bsShopSettings.hostname}
                        onChange={(value) => setBsShopSettings((bsShopSettings) => {
                            return {
                                ...bsShopSettings, hostname: value,
                            };
                        })}
                    />

                    <BsShopIntegrationFormInput
                        type={'integer'}
                        label={t('port')}
                        value={bsShopSettings.port}
                        onChange={(value) => setBsShopSettings((bsShopSettings) => {
                            return {
                                ...bsShopSettings, port: value,
                            };
                        })}
                        note
                    />

                    <BsShopIntegrationFormInput
                        type={'text'}
                        label={t('auth.user')}
                        value={bsShopSettings.username}
                        onChange={(value) => setBsShopSettings((bsShopSettings) => {
                            return {
                                ...bsShopSettings, username: value,
                            };
                        })}
                    />

                    <BsShopIntegrationFormInput
                        type={'password'}
                        label={t('auth.password')}
                        value={bsShopSettings.password}
                        note={projectIntegration === null ? '' : t('project.leave-empty-to-not-change')}
                        onChange={(value) => setBsShopSettings((bsShopSettings) => {
                            return {
                                ...bsShopSettings, password: value,
                            };
                        })}
                    />

                    <div className={'min-h-4 max-h-4'}>{/* spacer */}</div>

                    <BsShopIntegrationFormInput
                        type={'text'}
                        label={t('database.database-name')}
                        value={bsShopSettings.databaseName}
                        onChange={(value) => setBsShopSettings((bsShopSettings) => {
                            return {
                                ...bsShopSettings, databaseName: value,
                            };
                        })}
                    />

                    {projectIntegration && <>
                        <div className={'min-h-1 max-h-1'}>{/* spacer */}</div>

                        <div className={'w-full'}>
                            {t('bs-shop.pohoda.enabled-b2c-centres')}:
                        </div>

                        <div className={'w-10/12 mx-auto'}>
                            <Select
                                defaultValue={defaultB2CCentres}
                                isMulti
                                name="enabledCentres"
                                options={centreOptions}
                                className="basic-multi-select"
                                classNamePrefix="select"
                                onChange={setSelectedB2CCentres}
                            />
                        </div>

                        <div className={'w-full'}>
                            {t('bs-shop.pohoda.enabled-b2b-centres')}:
                        </div>

                        <div className={'w-10/12 mx-auto'}>
                            <Select
                                defaultValue={defaultB2BCentres}
                                isMulti
                                name="enabledCentres"
                                options={centreOptions}
                                className="basic-multi-select"
                                classNamePrefix="select"
                                onChange={setSelectedB2BCentres}
                            />
                        </div>

                        <div className={'w-full'}>
                            {t('bs-shop.pohoda.excluded-email')}:
                        </div>

                        <div className={'w-10/12 mx-auto'}>
                        <textarea
                            value={bsShopSettings.skippedEmailAddressList}
                            name="skipped-emails"
                            className={'form-textarea w-full'}
                            onChange={(e) => setBsShopSettings((bsShopSettings) => {
                                return {
                                    ...bsShopSettings, skippedEmailAddressList: e.target.value,
                                };
                            })}
                        />
                            <i>{t('bs-shop.pohoda.excluded-email-delimiter-info')}</i>
                        </div>

                    </>}

                    <div className={'min-h-4 max-h-4'}>{/* spacer */}</div>

                    <div className={'flex justify-end'}>
                        <Button
                            className={'button-primary'}
                            onClick={() => setButtonClicked(true)}
                            title={t('project.integration-create')}
                        >
                            {projectIntegration
                                ? t('project.integration-update')
                                : t('project.integration-create')}
                        </Button>
                    </div>
                </div>
            </LoadingComponent>
        </div>
    );
}
